import React from 'react';

import {
  ArrowBack,
  ArrowForward,
  HomeWorkOutlined,
  StarOutlined,
} from '@mui/icons-material';
import { Avatar, IconButton, Skeleton, Typography } from '@mui/material';

import { AppServicesContext } from '@klokhuis/core/AppServices';
import { SellerWithListingCount } from '@klokhuis/core/ApiClient/Klokhuis';
import { useIntl } from '@klokhuis/core/Intl';
import { PageLayout } from '@klokhuis/core/PageLayout';

import styles from './TopPartners.module.css';
import { useMediaIsDesktop } from '@klokhuis/utilities/Media/useMediaIsDesktop';
import { CommonStyles } from '@klokhuis/styles/CommonStyles';

const MIN_CARDS = 4;

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map(n => n[0]?.toUpperCase() ?? '')
    .join('');

const formatMemberSince = (memberSince: string | null | undefined): string => {
  if (!memberSince) return '';
  const year = new Date(memberSince).getFullYear();
  return isNaN(year) ? memberSince : String(year);
};

interface SellerCardProps {
  seller: SellerWithListingCount;
  T: (key: string, ...args: unknown[]) => string;
}

const SellerCard = ({ seller, T }: SellerCardProps) => {
  const memberYear = formatMemberSince(seller.memberSince);
  const displayLabel = seller.fullName ?? seller.displayName;

  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <Avatar
          src={seller.profilePictureUrl ?? undefined}
          className={styles.avatar}
          sx={{
            bgcolor: 'var(--design-color-primary-accent)',
            color: 'var(--design-color-primary-main)',
            fontWeight: 700,
          }}
        >
          {getInitials(displayLabel)}
        </Avatar>
      </div>

      <div className={styles.cardBody}>
        <Typography
          variant="subtitle1"
          className={styles.sellerName}
          title={displayLabel}
        >
          {displayLabel}
        </Typography>

        {seller.fullName && seller.displayName !== seller.fullName && (
          <Typography variant="caption" className={styles.agencyName}>
            {seller.displayName}
          </Typography>
        )}

        <div className={styles.metaRow}>
          <HomeWorkOutlined className={styles.metaIcon} />
          <Typography variant="body2" className={styles.metaText}>
            {T(
              'listings_page.top_partners.listings_count',
              seller.listingsCount,
            )}
          </Typography>
        </div>

        {memberYear && (
          <Typography variant="caption" className={styles.memberSince}>
            {T('listings_page.top_partners.member_since', memberYear)}
          </Typography>
        )}
      </div>
    </div>
  );
};

const PlaceholderCard = ({ T }: { T: (key: string) => string }) => (
  <div className={`${styles.card} ${styles.placeholderCard}`}>
    <div className={styles.cardTop}>
      <div className={styles.placeholderIconWrap}>
        <StarOutlined className={styles.placeholderIcon} />
      </div>
    </div>

    <div className={styles.cardBody}>
      <Typography variant="subtitle1" className={styles.sellerName}>
        {T('listings_page.top_partners.placeholder_name')}
      </Typography>
      <Typography variant="body2" className={styles.placeholderDesc}>
        {T('listings_page.top_partners.placeholder_description')}
      </Typography>
    </div>
  </div>
);

const SkeletonCard = () => (
  <div className={styles.card}>
    <div className={styles.cardTop}>
      <Skeleton
        variant="circular"
        width={64}
        height={64}
        className={styles.avatar}
      />
    </div>
    <div className={styles.cardBody}>
      <Skeleton variant="text" width="80%" height={24} />
      <Skeleton
        variant="text"
        width="60%"
        height={20}
        style={{ marginTop: 8 }}
      />
      <Skeleton
        variant="text"
        width="50%"
        height={16}
        style={{ marginTop: 4 }}
      />
    </div>
  </div>
);

export const TopPartners = () => {
  const { T } = useIntl();
  const { apiClient } = AppServicesContext.useContext();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const isDesktop = useMediaIsDesktop();

  const [sellers, setSellers] = React.useState<SellerWithListingCount[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    apiClient.listing
      .getAllSellersWithListingCounts()
      .then(res => {
        if (!cancelled) setSellers(res.sellers ?? []);
      })
      .catch(() => {
        if (!cancelled) setSellers([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient.listing]);

  const placeholdersNeeded = Math.max(0, MIN_CARDS - sellers.length);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -280 : 280,
        behavior: 'smooth',
      });
    }
  };

  return (
    <PageLayout.SectionContainer containerClassName={styles.root}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Typography
            variant={isDesktop ? 'h4' : 'h5'}
            className={CommonStyles.padding_bottom_2}
          >
            {T('listings_page.top_partners')}
          </Typography>
          <Typography
            variant={isDesktop ? 'subtitle1' : 'caption'}
            className={styles.subtitle}
          >
            {T('listings_page.top_partners.subtitle')}
          </Typography>
        </div>

        <div className={styles.controls}>
          <IconButton
            className={styles.controlBtn}
            onClick={() => scroll('left')}
            size="small"
            aria-label="scroll left"
          >
            <ArrowBack className={styles.arrowIcon} />
          </IconButton>
          <IconButton
            className={styles.controlBtn}
            onClick={() => scroll('right')}
            size="small"
            aria-label="scroll right"
          >
            <ArrowForward className={styles.arrowIcon} />
          </IconButton>
        </div>
      </div>

      <div className={styles.scrollContainer} ref={scrollRef}>
        {isLoading ? (
          Array(MIN_CARDS)
            .fill(0)
            .map((_, i) => <SkeletonCard key={`sk-${i}`} />)
        ) : (
          <>
            {sellers.map(seller => (
              <SellerCard key={seller.sellerId} seller={seller} T={T} />
            ))}
            {Array(placeholdersNeeded)
              .fill(0)
              .map((_, i) => (
                <PlaceholderCard key={`ph-${i}`} T={T} />
              ))}
          </>
        )}
      </div>
    </PageLayout.SectionContainer>
  );
};
